import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Form, 
  Button, 
  Alert, 
  Spinner,
  Badge,
  ButtonGroup
} from 'react-bootstrap';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Swal from 'sweetalert2';

// Components
import ImageUpload from './components/ImageUpload';
import TinyMCEEditor from './components/TinyMCEEditor';

// Services
import MarketplaceBannersServices from './services/MarketplaceBannersServices';

// Styles
import './marketplace.css';

const MarketplaceBannerForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  // States
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditing);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    image: null,
    button_text: '',
    button_link: '',
    is_active: true,
    sort_order: 0,
    start_date: '',
    end_date: ''
  });
  const [fieldErrors, setFieldErrors] = useState({});

  // Load banner data if editing
  useEffect(() => {
    if (isEditing) {
      loadBannerData();
    }
  }, [id, isEditing]);

  const loadBannerData = async () => {
    try {
      setInitialLoading(true);
      const response = await MarketplaceBannersServices.getBanner(id);
      const banner = response.data;
      
      setFormData({
        title: banner.title || '',
        subtitle: banner.subtitle || '',
        image: banner.image || null,
        button_text: banner.button_text || '',
        button_link: banner.button_link || '',
        is_active: banner.is_active ?? true,
        sort_order: banner.sort_order || 0,
        start_date: banner.start_date ? banner.start_date.slice(0, 16) : '',
        end_date: banner.end_date ? banner.end_date.slice(0, 16) : ''
      });
    } catch (error) {
      console.error('Erro ao carregar banner:', error);
      setError('Erro ao carregar dados do banner. Tente novamente.');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleImageChange = (imageData) => {
    setFormData(prev => ({
      ...prev,
      image: imageData
    }));
    
    if (fieldErrors.image) {
      setFieldErrors(prev => ({
        ...prev,
        image: null
      }));
    }
  };

  const validateForm = () => {
    const validation = MarketplaceBannersServices.validateBannerData(formData);
    
    if (!validation.isValid) {
      const errors = {};
      validation.errors.forEach(error => {
        if (error.includes('Título')) errors.title = error;
        if (error.includes('Subtítulo')) errors.subtitle = error;
        if (error.includes('Texto do botão')) errors.button_text = error;
        if (error.includes('Link do botão')) errors.button_link = error;
        if (error.includes('Data de fim')) errors.end_date = error;
      });
      setFieldErrors(errors);
      return false;
    }

    // Validate image for new banners
    if (!isEditing && !formData.image) {
      setFieldErrors({ image: 'Imagem é obrigatória' });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setFieldErrors({});

      const submitData = MarketplaceBannersServices.prepareBannerData(formData);
      
      // Prepare FormData for file upload
      const formDataToSend = new FormData();
      
      // Add all fields to FormData
      Object.keys(submitData).forEach(key => {
        if (submitData[key] !== null && submitData[key] !== undefined) {
          formDataToSend.append(key, submitData[key]);
        }
      });

      // Handle image upload
      if (formData.image?.file) {
        formDataToSend.append('image', formData.image.file);
      }

      let response;
      if (isEditing) {
        response = formData.image?.file 
          ? await MarketplaceBannersServices.updateBannerWithFile(id, formDataToSend)
          : await MarketplaceBannersServices.updateBanner(id, submitData);
      } else {
        response = await MarketplaceBannersServices.createBannerWithFile(formDataToSend);
      }

      // Success message
      await Swal.fire({
        icon: 'success',
        title: isEditing ? 'Banner Atualizado' : 'Banner Criado',
        text: isEditing 
          ? 'O banner foi atualizado com sucesso.'
          : 'O banner foi criado com sucesso.',
        timer: 3000,
        showConfirmButton: false
      });

      // Redirect to banners list
      navigate('/admin/marketplace/banners');

    } catch (error) {
      console.error('Erro ao salvar banner:', error);
      
      if (error.response?.data?.field_errors) {
        setFieldErrors(error.response.data.field_errors);
      } else {
        const errorMessage = error.response?.data?.detail 
          || error.response?.data?.error 
          || 'Erro ao salvar banner. Tente novamente.';
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/marketplace/banners');
  };

  if (initialLoading) {
    return (
      <Container fluid className="py-4">
        <Card>
          <Card.Body className="text-center py-5">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <h5>Carregando banner...</h5>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h3 mb-1 fw-bold">
            <i className="las la-image text-primary me-2" />
            {isEditing ? 'Editar Banner' : 'Novo Banner'}
          </h1>
          <p className="text-muted mb-0">
            {isEditing ? 'Atualize as informações do banner' : 'Crie um novo banner promocional para o marketplace'}
          </p>
        </div>
        
        <Button
          as={Link}
          to="/admin/marketplace/banners"
          variant="outline-secondary"
        >
          <i className="las la-arrow-left me-1" />
          Voltar
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <Alert.Heading>
            <i className="las la-exclamation-triangle me-2" />
            Erro
          </Alert.Heading>
          {error}
        </Alert>
      )}

      <Row>
        {/* Form */}
        <Col lg={8}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <i className="las la-edit me-2" />
                Informações do Banner
              </h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                {/* Title */}
                <Form.Group className="mb-3">
                  <Form.Label>
                    Título <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Digite o título do banner"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    isInvalid={!!fieldErrors.title}
                    disabled={loading}
                    maxLength={200}
                  />
                  <Form.Control.Feedback type="invalid">
                    {fieldErrors.title}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    Máximo 200 caracteres
                  </Form.Text>
                </Form.Group>

                {/* Subtitle */}
                <Form.Group className="mb-3">
                  <Form.Label>Subtítulo</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Digite o subtítulo do banner (opcional)"
                    value={formData.subtitle}
                    onChange={(e) => handleInputChange('subtitle', e.target.value)}
                    isInvalid={!!fieldErrors.subtitle}
                    disabled={loading}
                    maxLength={300}
                  />
                  <Form.Control.Feedback type="invalid">
                    {fieldErrors.subtitle}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    Máximo 300 caracteres
                  </Form.Text>
                </Form.Group>

                {/* Image Upload */}
                <ImageUpload
                  label="Imagem do Banner"
                  value={formData.image}
                  onChange={handleImageChange}
                  error={fieldErrors.image}
                  disabled={loading}
                  required={!isEditing}
                  maxSizeMB={5}
                  maxWidth={1200}
                  maxHeight={400}
                  helpText="Dimensões recomendadas: 1200x400px. A imagem será redimensionada automaticamente."
                />

                {/* Button Text */}
                <Form.Group className="mb-3">
                  <Form.Label>Texto do Botão</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ex: Ver Agentes, Saiba Mais, etc."
                    value={formData.button_text}
                    onChange={(e) => handleInputChange('button_text', e.target.value)}
                    isInvalid={!!fieldErrors.button_text}
                    disabled={loading}
                    maxLength={100}
                  />
                  <Form.Control.Feedback type="invalid">
                    {fieldErrors.button_text}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    Máximo 100 caracteres. Se preenchido, o link do botão torna-se obrigatório.
                  </Form.Text>
                </Form.Group>

                {/* Button Link */}
                <Form.Group className="mb-3">
                  <Form.Label>
                    Link do Botão
                    {formData.button_text && <span className="text-danger"> *</span>}
                  </Form.Label>
                  <Form.Control
                    type="url"
                    placeholder="https://exemplo.com/destino"
                    value={formData.button_link}
                    onChange={(e) => handleInputChange('button_link', e.target.value)}
                    isInvalid={!!fieldErrors.button_link}
                    disabled={loading}
                  />
                  <Form.Control.Feedback type="invalid">
                    {fieldErrors.button_link}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    URL completa para onde o botão deve levar
                  </Form.Text>
                </Form.Group>

                {/* Scheduling */}
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Data de Início</Form.Label>
                      <Form.Control
                        type="datetime-local"
                        value={formData.start_date}
                        onChange={(e) => handleInputChange('start_date', e.target.value)}
                        disabled={loading}
                      />
                      <Form.Text className="text-muted">
                        Quando o banner deve começar a aparecer (opcional)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Data de Fim</Form.Label>
                      <Form.Control
                        type="datetime-local"
                        value={formData.end_date}
                        onChange={(e) => handleInputChange('end_date', e.target.value)}
                        isInvalid={!!fieldErrors.end_date}
                        disabled={loading}
                      />
                      <Form.Control.Feedback type="invalid">
                        {fieldErrors.end_date}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        Quando o banner deve parar de aparecer (opcional)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Settings */}
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Ordem de Exibição</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        max="999"
                        value={formData.sort_order}
                        onChange={(e) => handleInputChange('sort_order', parseInt(e.target.value) || 0)}
                        disabled={loading}
                      />
                      <Form.Text className="text-muted">
                        Ordem de exibição (menor número aparece primeiro)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Status</Form.Label>
                      <div className="pt-2">
                        <Form.Check
                          type="switch"
                          id="is_active"
                          label={formData.is_active ? "Ativo" : "Inativo"}
                          checked={formData.is_active}
                          onChange={(e) => handleInputChange('is_active', e.target.checked)}
                          disabled={loading}
                        />
                        <Form.Text className="text-muted">
                          Banner inativo não será exibido no marketplace
                        </Form.Text>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Submit Buttons */}
                <div className="d-flex gap-2 justify-content-end mt-4">
                  <Button
                    type="button"
                    variant="outline-secondary"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        {isEditing ? 'Atualizando...' : 'Criando...'}
                      </>
                    ) : (
                      <>
                        <i className={`las la-${isEditing ? 'save' : 'plus'} me-1`} />
                        {isEditing ? 'Atualizar Banner' : 'Criar Banner'}
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* Preview */}
        <Col lg={4}>
          <Card className="sticky-top">
            <Card.Header>
              <h5 className="mb-0">
                <i className="las la-eye me-2" />
                Preview do Banner
              </h5>
            </Card.Header>
            <Card.Body>
              {/* Banner Preview */}
              <div className="banner-preview border rounded p-3">
                {formData.image ? (
                  <div className="banner-preview-image mb-3">
                    <img
                      src={formData.image?.preview || formData.image}
                      alt="Preview"
                      className="img-fluid rounded"
                      style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                    />
                  </div>
                ) : (
                  <div 
                    className="banner-preview-placeholder bg-light border rounded d-flex align-items-center justify-content-center mb-3"
                    style={{ height: '150px' }}
                  >
                    <div className="text-muted text-center">
                      <i className="las la-image" style={{ fontSize: '2rem' }} />
                      <div className="small mt-1">Imagem do banner</div>
                    </div>
                  </div>
                )}
                
                {formData.title && (
                  <h6 className="banner-preview-title fw-bold mb-2">
                    {formData.title}
                  </h6>
                )}
                
                {formData.subtitle && (
                  <p className="banner-preview-subtitle text-muted small mb-3">
                    {formData.subtitle}
                  </p>
                )}
                
                {formData.button_text && (
                  <Button
                    variant="primary"
                    size="sm"
                    disabled
                    className="banner-preview-button"
                  >
                    {formData.button_text}
                  </Button>
                )}
              </div>

              {/* Status Info */}
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="small text-muted">Status:</span>
                  <Badge bg={formData.is_active ? "success" : "secondary"}>
                    {formData.is_active ? "Ativo" : "Inativo"}
                  </Badge>
                </div>
                
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="small text-muted">Ordem:</span>
                  <span className="small">#{formData.sort_order}</span>
                </div>
                
                {(formData.start_date || formData.end_date) && (
                  <div className="small text-muted mt-2">
                    <i className="las la-clock me-1" />
                    Agendamento:
                    {formData.start_date && (
                      <div>Início: {new Date(formData.start_date).toLocaleString('pt-BR')}</div>
                    )}
                    {formData.end_date && (
                      <div>Fim: {new Date(formData.end_date).toLocaleString('pt-BR')}</div>
                    )}
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default MarketplaceBannerForm;